import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { X, Clock, User, Scissors, Calendar, FileText, AlertCircle } from 'lucide-react';
import { patchBookingStatus } from '../../lib/bookingsApi';

const TZ = 'America/New_York';

function fmtDate(iso) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    timeZone: TZ,
  }).format(new Date(iso));
}

function fmtTimeRange(iso, durationMin) {
  const start = new Date(iso);
  const end   = new Date(start.getTime() + (durationMin || 0) * 60_000);
  const fmt   = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

const STATUS_OPTS = [
  { value: 'confirmed',  label: 'Confirmed',  color: 'bg-emerald-100 text-emerald-700' },
  { value: 'pending',    label: 'Pending',    color: 'bg-amber-100 text-amber-700' },
  { value: 'completed',  label: 'Completed',  color: 'bg-blue-100 text-blue-700' },
  { value: 'cancelled',  label: 'Cancelled',  color: 'bg-red-100 text-red-700' },
  { value: 'no_show',   label: 'No-show',    color: 'bg-slate-100 text-slate-600' },
];

function Badge({ status }) {
  const opt = STATUS_OPTS.find(o => o.value === status) || STATUS_OPTS[0];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
      {opt.label}
    </span>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-surface-border last:border-0">
      <Icon size={15} className="text-ink-muted mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-ink-muted">{label}</p>
        <p className="text-sm text-ink font-medium">{value}</p>
      </div>
    </div>
  );
}

export default function BookingDetailModal({ booking, onClose, onRefresh }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(booking.status);

  const mutation = useMutation({
    mutationFn: (s) => patchBookingStatus(booking.id, s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] });
      onRefresh?.();
    },
  });

  const handleStatusChange = (s) => {
    setStatus(s);
    mutation.mutate(s);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <div>
            <h2 className="text-sm font-semibold text-ink">Booking Details</h2>
            <p className="text-xs text-ink-muted mt-0.5">#{booking.id.slice(0, 8).toUpperCase()}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-ink-muted transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">

          {/* Status */}
          <div className="flex items-center justify-between">
            <Badge status={status} />
            <select
              value={status}
              onChange={e => handleStatusChange(e.target.value)}
              className="text-xs border border-surface-border rounded-lg px-2 py-1.5 text-ink bg-white focus:outline-none focus:border-primary"
              disabled={mutation.isPending}
            >
              {STATUS_OPTS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {mutation.isError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertCircle size={14} />
              Failed to update status
            </div>
          )}

          {/* Details */}
          <div className="bg-surface-50 rounded-lg px-4 py-1">
            <Row icon={User}     label="Client"  value={booking.Client?.name} />
            <Row icon={Scissors} label="Service" value={booking.Service?.name} />
            <Row icon={User}     label="Staff"   value={booking.Staff?.name} />
            <Row icon={Calendar} label="Date"    value={fmtDate(booking.start_at)} />
            <Row icon={Clock}    label="Time"    value={fmtTimeRange(booking.start_at, booking.Service?.duration_minutes)} />
            {booking.Service?.price_cents > 0 && (
              <Row icon={FileText} label="Price" value={`$${(booking.Service.price_cents / 100).toFixed(2)}`} />
            )}
            {booking.notes && (
              <Row icon={FileText} label="Notes" value={booking.notes} />
            )}
          </div>

          {/* Client contact */}
          {(booking.Client?.email || booking.Client?.phone) && (
            <div className="text-xs text-ink-secondary space-y-1">
              {booking.Client.email && <p>✉ {booking.Client.email}</p>}
              {booking.Client.phone && <p>📞 {booking.Client.phone}</p>}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-surface-border flex justify-end">
          <button onClick={onClose} className="btn btn-secondary text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}
